import ThermalPrinter from "@/plugins/thermal-printer";
import { Capacitor } from "@capacitor/core";
import { formatOrderDate } from "@/utils/dateFormat";

/**
 * Convert a string to title case
 * Handles "ice level" -> "Ice Level", "ice_level" -> "Ice Level"
 * @param {string} str - String to convert
 * @returns {string} Title-cased string
 */
function toTitleCase(str) {
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Helper function to ensure printer is ready with retry logic
 * Attempts to rebind if printer is not ready, with exponential backoff
 * @param {number} maxAttempts - Maximum number of attempts (default: 3)
 * @returns {Promise<boolean>} True if printer is ready, false otherwise
 */
async function ensurePrinterReady(maxAttempts = 3) {
  const delays = [500, 1000, 2000]; // Exponential backoff delays

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const status = await ThermalPrinter.isReady();
      console.log(
        `[PrintService] Attempt ${attempt}: Printer status -`,
        status
      );

      if (status.ready && status.bound) {
        return true;
      }

      // If not ready, attempt force rebind
      console.log(
        `[PrintService] Attempt ${attempt}: Printer not ready, attempting force rebind`
      );
      const rebindResult = await ThermalPrinter.forceRebind();
      console.log(
        `[PrintService] Attempt ${attempt}: Force rebind result -`,
        rebindResult
      );

      if (rebindResult.success && rebindResult.ready) {
        return true;
      }

      // Wait before next attempt (with exponential backoff)
      if (attempt < maxAttempts) {
        const delay = delays[attempt - 1] || 2000;
        console.log(`[PrintService] Waiting ${delay}ms before retry`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`[PrintService] Attempt ${attempt} failed:`, error);
      if (attempt < maxAttempts) {
        const delay = delays[attempt - 1] || 2000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  console.error(
    `[PrintService] Failed to ensure printer ready after ${maxAttempts} attempts`
  );
  return false;
}

/**
 * Order Print Service
 * Handles automatic printing of order tickets for shop admin
 */

/**
 * Format order ticket for thermal printer
 * @param {Object} order - Order object from API
 * @param {Array} cartOrders - Cart order items with product details
 * @returns {Array} Array of print commands for thermal printer
 */
export function formatOrderTicket(order, cartOrders) {
  const printCommands = [];

  // Header separator
  printCommands.push({
    text: "================================",
    fontSize: 20,
    bold: false,
    alignment: "CENTER",
  });

  // Customer name (large, bold, centered)
  printCommands.push({
    text: (order?.customerInfo?.fullName || "UNKNOWN").toUpperCase(),
    fontSize: 32,
    bold: true,
    alignment: "CENTER",
  });

  // Order number (short format)
  const uuid = order?.uuid || "";
  const orderNumber = uuid
    ? uuid.replace(/-/gi, "").toUpperCase().substring(0, 6)
    : "XXXXXX";
  printCommands.push({
    text: `Order #: ${orderNumber}  ${
      order?.dateTimeOrdered ? formatOrderDate(order.dateTimeOrdered) : ""
    }`,
    fontSize: 24,
    bold: false,
    alignment: "CENTER",
  });
  // Items separator
  printCommands.push({
    text: "--------------------------------",
    fontSize: 20,
    bold: false,
    alignment: "CENTER",
  });

  // Each order item
  cartOrders.forEach((cartOrder) => {
    // Item line: quantity and name
    printCommands.push({
      text: `${cartOrder.quantity}x ${cartOrder.product.productName}`,
      fontSize: 24,
      bold: true,
      alignment: "LEFT",
    });

    // Variations (milk, size, sugar, etc.)
    // Supports both array format (new) and object format (legacy)
    if (cartOrder.variations) {
      const variationsArray = Array.isArray(cartOrder.variations)
        ? cartOrder.variations
        : Object.values(cartOrder.variations);

      variationsArray.forEach((variation) => {
        if (variation && variation.name) {
          // Build variation text with type prefix if available
          // Use type (new format) or category (legacy) for the label
          const typeLabel = variation.type || variation.category;
          let variationText;
          if (typeLabel) {
            // Title-case the type for thermal print
            // Handles "ice level" -> "Ice Level", "ice_level" -> "Ice Level"
            const formattedType = toTitleCase(typeLabel);
            variationText = `   ${formattedType}: ${variation.name}`;
          } else {
            variationText = `   ${variation.name}`;
          }

          printCommands.push({
            text: variationText,
            fontSize: 20,
            bold: false,
            alignment: "LEFT",
          });
        }
      });
    }

    // Special instructions
    if (cartOrder.instructions && cartOrder.instructions.trim() !== "") {
      printCommands.push({
        text: `Special: ${cartOrder.instructions}`,
        fontSize: 20,
        bold: true,
        alignment: "LEFT",
      });
    }
  });

  // Footer separator
  printCommands.push({
    text: "================================",
    fontSize: 20,
    bold: false,
    alignment: "CENTER",
  });

  return printCommands;
}

/**
 * Print order ticket
 * @param {Object} order - Order object from API
 * @param {Array} cartOrders - Cart order items with product details
 * @returns {Promise<Object>} Result object with success status and message
 */
export async function printOrderTicket(order, cartOrders) {
  try {
    // Check if running on native platform (iOS or Android)
    const platform = Capacitor.getPlatform();
    if (platform === "web") {
      console.log(
        "Thermal printer not available on web platform - skipping print"
      );
      return {
        success: false,
        message: "Printing is only available on mobile devices",
        skipped: true,
      };
    }

    // Ensure printer is ready with retry logic
    // This handles cases where printer connection was lost after app force-kill
    const printerReady = await ensurePrinterReady(3);
    if (!printerReady) {
      return {
        success: false,
        message:
          "Printer not ready. Please restart the app or check printer connection.",
      };
    }

    // Format order ticket
    const printCommands = formatOrderTicket(order, cartOrders);

    // Add extra line feed at the end
    printCommands.push({
      text: "\n\n",
      fontSize: 20,
      bold: false,
      alignment: "LEFT",
    });

    // Print all items in a single batch (more efficient and reliable)
    console.log(
      `[PrintService] Sending batch of ${printCommands.length} items to printer`
    );
    const printResult = await ThermalPrinter.printBatch({
      items: printCommands,
    });
    console.log("[PrintService] Batch print result:", printResult);

    if (!printResult.success) {
      return {
        success: false,
        message: "Print failed",
      };
    }

    return {
      success: true,
      message: `Order ticket printed for ${
        order?.customerInfo?.fullName || "UNKNOWN"
      }`,
    };
  } catch (error) {
    console.error("Failed to print order ticket:", error);
    return {
      success: false,
      message: error.message || "Print failed",
    };
  }
}

export default {
  formatOrderTicket,
  printOrderTicket,
};
